import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProjectType, Undefinable } from '@symfa-inc/providence-verizon-types';
import { Form, Row } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { HttpService } from '@core/services';
import { ProjectManagementHttpService } from '@core/services/http';
import { useDidUpdateEffect } from '@core/utils/hooks';
import { arrsToSelectableObject } from '@core/utils/methods';
import {
  BROWSE_DEFAULT_FILTER_VALUE,
  browseFiltersChange,
  tableCount,
} from '@models/constants';
import {
  AdminDropdownValuesData,
  FiltersSetData,
  FiltersSetDataExtended,
  FiltersSetDataFilter,
} from '@models/interfaces';
import { MainProjectManagementBrowseFilter, SwitchType } from '@models/types';
import { Autocomplete } from '@shared/modules';
import { BrowsePage } from '@shared/modules/browse-page';
import { UserActions } from '@store/actions';
import store from '@store/index';
import {
  ProjectManagementSelectors,
  SharedSelectors,
  UserSelectors,
} from '@store/selectors';
import {
  convertPaceTaskFilterToObj,
  DEFAULT_FILTERS_SET_VALUE,
} from './filters/models';
import {
  findFiltersSet,
  getNewColumnsSettings,
  getProjectManagementBrowseColumns,
  projectManagementBrowseFilterTransformer,
} from './models/constants';
import { Filters } from './filters';

import './styles.scss';

const { useForm } = Form;

export const ProjectManagementBrowse: FC = () => {
  const [form] = useForm();

  const projectManagementService = useMemo(
    () => HttpService.getHttpRequests(ProjectManagementHttpService),
    [],
  );

  const {
    projectManagementBrowse,
    filterSets,
    pmScreenPaceTasksOptions,
    settingsColumns,
  } = useSelector(ProjectManagementSelectors.getPMBrowseInfo);
  const currentProjectType = useSelector(UserSelectors.getDefaultPMProjectType);
  const { dropdownValues } = useSelector(
    SharedSelectors.getDropdownValuesAndStates,
  );

  // filters states
  const [selectorValue, setSelectorValue] =
    useState<Undefinable<SelectValue>>(undefined);
  const [filtersSet, setFiltersSet] = useState<FiltersSetDataExtended>(
    DEFAULT_FILTERS_SET_VALUE,
  );
  // remove it when cancel button props func will be changed
  const [needClear, setNeedClear] = useState<boolean>(false);
  //
  const [filterSetFields, setFilterSetFields] = useState<FiltersSetDataFilter>(
    {},
  );
  const [filterValue, setFilterValue] =
    useState<MainProjectManagementBrowseFilter>({
      ...BROWSE_DEFAULT_FILTER_VALUE,
      showCompleted: false,
    });
  const [projectType, setProjectType] =
    useState<ProjectType>(currentProjectType);

  useDidUpdateEffect(() => {
    const { name, ...fieldsWithoutName } = filterSetFields;

    projectManagementService.getProjectManagementBrowse({
      ...filterValue,
      configuredFilter:
        projectManagementBrowseFilterTransformer(fieldsWithoutName),
      projectType,
    });
  }, [filterValue, filterSetFields, projectType]);

  const onSearchChanges = (search: string): void => {
    setFilterValue((prevState: MainProjectManagementBrowseFilter) => ({
      ...prevState,
      search,
    }));
  };

  const onApplyConfigureFilter = async (): Promise<void> => {
    const formValue = form.getFieldsValue();
    const { name, ...rest } = formValue;

    if (name) {
      await projectManagementService.updatePMBrowseFiltersSet({
        ...projectManagementBrowseFilterTransformer(rest),
        id: selectorValue,
        name,
      } as FiltersSetData);
    }

    setFilterSetFields({
      name,
      ...Object.keys(rest).reduce((acc: FiltersSetDataFilter, key: string) => {
        const { visible, ...otherData } = rest[key];

        return { ...acc, [key]: otherData };
      }, {}),
    });

    store.dispatch(
      UserActions.changeColumnsSettings.done({
        module: 'projectManagement',
        columns: getNewColumnsSettings(
          settingsColumns.projectManagement,
          rest,
          pmScreenPaceTasksOptions,
        ),
      }),
    );
  };

  const onOnlyCompletedSwitch = (value: boolean): void =>
    setFilterValue((prevState: MainProjectManagementBrowseFilter) => ({
      ...prevState,
      showCompleted: value as SwitchType,
    }));

  // remove it when cancel button props func will be changed
  useEffect(() => {
    const { name, filter, paceTaskFilter } = filtersSet;

    form.resetFields();
    form.setFieldsValue({
      name,
      ...filter,
      ...convertPaceTaskFilterToObj(paceTaskFilter),
    });
  }, [needClear]);

  return (
    <BrowsePage
      switchInfo="Show Completed Projects"
      columns={getProjectManagementBrowseColumns(
        Object.keys({
          ...filtersSet.filter,
          ...convertPaceTaskFilterToObj(filtersSet.paceTaskFilter),
        }),
        selectorValue,
      )}
      data={projectManagementBrowse?.items}
      filters={
        <Filters
          formProps={{ form }}
          selectorValue={selectorValue}
          setSelectorValue={setSelectorValue}
          setFiltersSet={setFiltersSet}
          filtersSet={filtersSet}
        />
      }
      place={tableCount(true, 'Project')}
      filterFormProps={{ form, labelCol: { span: 9 } }}
      validation
      additional={
        <Row className="prov-project-management__filters-type">
          <Autocomplete
            id="projectTypeId"
            key="projectTypeId"
            className="left"
            label="Project Type"
            options={arrsToSelectableObject<AdminDropdownValuesData>(
              dropdownValues.ProjectType,
              'id',
              'value',
            )}
            elementProps={{
              allowClear: true,
              value: projectType,
              onChange: (value: SelectValue): void =>
                setProjectType(value as ProjectType),
            }}
          />
        </Row>
      }
      className="prov-project-management"
      cancelButtonProps={{
        onClick: (): void => {
          setFiltersSet(findFiltersSet(filterSets, selectorValue));
          setNeedClear((prevValue: boolean) => !prevValue);
        },
      }}
      onChange={browseFiltersChange(setFilterValue)}
      onSearchChanges={onSearchChanges}
      onApplyFilter={onApplyConfigureFilter}
      onSwitch={onOnlyCompletedSwitch}
      tableProps={{
        pagination: {
          total: projectManagementBrowse?.total,
        },
        module: 'projectManagement',
      }}
    />
  );
};
